/**
 * OrientationDocCard — one kit document on the public orientation page: view, fill (if fillable), name + signature, submit.
 */
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, FileText, Loader2, PenTool } from "lucide-react";
import { SignaturePad } from "@/components/orientation/SignaturePad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrientationDocCard({ doc: d, entry, filled, signerName, onSignerName, onSignature, onFill, onSign, saving, showTitle = true }) {
  const signed = !!entry?.signed_at;
  const style = d.signature_style || "esign";
  const canAck = style === "acknowledge" || style === "esign_or_ack";
  const canEsign = style === "esign" || style === "esign_or_ack";
  const isFillable = !!d.fillable_form?.enabled && (d.fillable_form.fields || []).length > 0;

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50" data-testid={`orientation-doc-${d.id}`}>
      {showTitle && (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">{d.title}</p>
            {d.description && <p className="text-sm text-slate-500">{d.description}</p>}
          </div>
          {signed && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
              <CheckCircle className="w-3 h-3" /> Signed
            </Badge>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <a href={`${BACKEND_URL}${d.document_url}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
          <FileText className="w-3 h-3" /> View / download document
        </a>
        {isFillable && !signed && (
          <Button
            size="sm"
            variant={filled ? "outline" : "default"}
            onClick={onFill}
            className={`gap-1 ${filled ? "border-emerald-300 text-emerald-700" : "bg-purple-600 hover:bg-purple-700"}`}
            data-testid={`fill-form-${d.id}`}
          >
            {filled ? <CheckCircle className="w-3.5 h-3.5" /> : <PenTool className="w-3.5 h-3.5" />}
            {filled ? "Form filled — edit answers" : "Fill out this form"}
          </Button>
        )}
        {isFillable && signed && entry?.filled && (
          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">Form completed</Badge>
        )}
        {signed && d.kind === "inservice" && (
          <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200" data-testid={`inservice-sent-${d.id}`}>Sent to your agency office</Badge>
        )}
      </div>
      {isFillable && !signed && !filled && (
        <p className="text-xs text-purple-700" data-testid={`fill-first-${d.id}`}>
          Step 1: fill out the form above. Step 2: type your name and sign below.
        </p>
      )}

      {!signed && (
        <div className="space-y-3 pt-2 border-t border-slate-200">
          <div>
            <Label className="text-xs">Your full name</Label>
            <Input value={signerName || ""} onChange={(e) => onSignerName(e.target.value)} placeholder="Type your legal name" data-testid={`signer-name-${d.id}`} />
          </div>
          {canEsign && (
            <div>
              <Label className="text-xs mb-1 block">Draw your signature</Label>
              <SignaturePad onDataUrl={onSignature} />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {canEsign && (
              <Button onClick={() => onSign(false)} disabled={saving} className="gap-1" data-testid={`sign-${d.id}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenTool className="w-4 h-4" />}
                Submit signature
              </Button>
            )}
            {canAck && (
              <Button variant="outline" onClick={() => onSign(true)} disabled={saving} className="gap-1" data-testid={`ack-${d.id}`}>
                <CheckCircle className="w-4 h-4" />
                I acknowledge
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
